import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  ArrowSync20Regular,
  PeopleSwap20Regular,
  Filter20Regular,
  Warning20Regular,
  Open20Regular,
  Search20Regular,
  LockClosed16Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { CountChips } from "../components/duplicates/RelatedRecords";
import { useAuth } from "../context/AuthContext";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import {
  DuplicateCluster,
  DuplicateCandidateStatus,
  ClusterContactSnapshot,
  formatReasonCode,
  listDuplicateClusters,
  listDuplicateTeams,
  refreshDuplicateCandidates,
} from "../services/duplicateCandidateService";

const PAGE_SIZE = 30;

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  toolbarRow: {
    display: "flex",
    columnGap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  toolbarBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "rgba(255,255,255,0.08)",
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.18)"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.16)",
      ...shorthands.borderColor(BRAND.gold),
    },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  card: {
    backgroundColor: BRAND.bg,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "12px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  scoreCircle: {
    width: "44px",
    height: "44px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("2px", "solid", BRAND.purple),
    fontFamily: BRAND.fontAccent,
    fontWeight: 700,
    fontSize: "1rem",
  },
  scoreWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "2px",
    flexShrink: 0,
    cursor: "help",
  },
  scoreCaption: {
    fontSize: "0.58rem",
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  lockBadge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    marginLeft: "8px",
    color: "#8a6d00",
    backgroundColor: "#fdf3d6",
    ...shorthands.border("1px", "solid", "#e8cf7a"),
    ...shorthands.borderRadius("999px"),
    paddingLeft: "8px",
    paddingRight: "8px",
    paddingTop: "1px",
    paddingBottom: "1px",
    fontSize: "0.72rem",
    fontWeight: 600,
  },
  headerReasons: { color: BRAND.muted, fontSize: "0.8rem", flexGrow: 1 },
  members: { display: "flex", flexDirection: "column", rowGap: "8px" },
  member: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  memberMain: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    flexGrow: 1,
  },
  memberNameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  memberName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
  },
  nbrBadge: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: BRAND.purple,
    backgroundColor: "#f0e6f2",
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: "7px",
    paddingRight: "7px",
    ...shorthands.borderRadius("999px"),
  },
  typeBadge: {
    fontSize: "0.68rem",
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("4px"),
    paddingLeft: "5px",
    paddingRight: "5px",
  },
  memberMeta: { color: BRAND.muted, fontSize: "0.78rem" },
  boTeamLine: { color: BRAND.ink, fontSize: "0.84rem", fontWeight: 700 },
  memberDeleted: { opacity: 0.6, backgroundColor: "#f6f4f7" },
  memberNameStruck: { textDecorationLine: "line-through" },
  survivorTag: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.purple,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  searchInput: {
    flexGrow: 1,
    appearance: "none",
    ...shorthands.border("0", "solid", "transparent"),
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    outlineStyle: "none",
  },
  actions: {
    display: "flex",
    columnGap: "6px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  actionBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("6px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  actionBtnPrimary: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": {
      backgroundColor: BRAND.ink,
      ...shorthands.borderColor(BRAND.ink),
    },
  },
  empty: {
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "20px",
    paddingBottom: "20px",
    textAlign: "center",
  },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
});

const FILTERS: Array<{ key: DuplicateCandidateStatus | "all"; label: string }> = [
  { key: "open", label: "Offen" },
  { key: "merged", label: "Zusammengeführt" },
  { key: "not_a_duplicate", label: "Kein Duplikat" },
  { key: "all", label: "Alle" },
];

const nameOf = (c: ClusterContactSnapshot): string =>
  c.displayName ||
  [c.lastname, c.firstName].filter(Boolean).join(", ") ||
  (c.contactNbrInt
    ? `Kunde Nr. ${c.contactNbrInt}`
    : c.contactNbrExt
      ? `Kunde Nr. ${c.contactNbrExt}`
      : `#${c.id}`);

/** Score → confidence tier (label + color) for the match-score badge. */
const scoreTier = (score: number): { color: string; label: string } => {
  if (score >= 80) return { color: BRAND.purple, label: "hoch" };
  if (score >= 50) return { color: "#b8860b", label: "mittel" };
  return { color: BRAND.muted, label: "gering" };
};

const addrOf = (c: ClusterContactSnapshot): string | undefined => {
  const a = c.defaultAddress;
  if (!a) return undefined;
  return (
    [a.street, a.postalCode, a.city].filter(Boolean).join(", ") || undefined
  );
};

const ContactDuplicatesPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { isAdmin, teams: myTeams } = useAuth();
  const [filter, setFilter] = useState<DuplicateCandidateStatus | "all">("open");
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [q, setQ] = useState(initialQ);
  const [qDebounced, setQDebounced] = useState(initialQ.trim());
  // Kontaktverantwortlicher (BO-Team) filter: "" = Alle, "none" = ohne Verantwortl.
  // Über die Session gemerkt: Detail → zurück zur Übersicht behält die Auswahl.
  const [teamId, setTeamId] = useState<string>(
    () => sessionStorage.getItem("duplicates_teamId") ?? "",
  );
  const setTeamFilter = (v: string) => {
    setTeamId(v);
    sessionStorage.setItem("duplicates_teamId", v);
  };
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);
  // Default the filter to the user's own team once (team members land on their
  // own team automatically; users without a team stay on "Alle").
  const teamDefaultedRef = useRef(false);
  const [clusters, setClusters] = useState<DuplicateCluster[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Load the BO teams once for the Verantwortlich-Filter dropdown.
  useEffect(() => {
    listDuplicateTeams()
      .then(setTeams)
      .catch(() => undefined);
  }, []);

  // Default the filter to the user's own team (once, when memberships arrive).
  useEffect(() => {
    // Nur auf das eigene Team defaulten, wenn der Nutzer in dieser Session noch
    // keine Auswahl getroffen hat — sonst die gemerkte Auswahl beibehalten.
    if (
      !teamDefaultedRef.current &&
      myTeams.length > 0 &&
      sessionStorage.getItem("duplicates_teamId") === null
    ) {
      teamDefaultedRef.current = true;
      setTeamId(myTeams[0].id);
    }
  }, [myTeams]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const { clusters: data, total: t } = await listDuplicateClusters(
        filter,
        PAGE_SIZE,
        0,
        qDebounced || undefined,
        controller.signal,
        teamId || undefined,
      );
      setClusters(data);
      setTotal(t);
      setHasMore(data.length < t);
    } catch (e: any) {
      if (e?.name !== "AbortError") setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [filter, qDebounced, teamId]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const fetchMore = async () => {
    try {
      const { clusters: data, total: t } = await listDuplicateClusters(
        filter,
        PAGE_SIZE,
        clusters.length,
        qDebounced || undefined,
        undefined,
        teamId || undefined,
      );
      setClusters((prev) => [...prev, ...data]);
      setTotal(t);
      setHasMore(clusters.length + data.length < t);
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setHasMore(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await refreshDuplicateCandidates();
      await load();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setRefreshing(false);
    }
  };

  const renderMember = (c: ClusterContactSnapshot) => {
    const addr = addrOf(c);
    const deleted = !!c.isDeleted;
    return (
      <div
        key={c.id}
        className={mergeClasses(styles.member, deleted && styles.memberDeleted)}
      >
        <div className={styles.memberMain}>
          <div className={styles.memberNameRow}>
            <span
              className={mergeClasses(styles.memberName, deleted && styles.memberNameStruck)}
            >
              {nameOf(c)}
            </span>
            {c.contactNbrInt && (
              <span className={styles.nbrBadge}>Nr. {c.contactNbrInt}</span>
            )}
            {c.type && <span className={styles.typeBadge}>{contactTypeLabel(c.type)}</span>}
            {deleted && (
              <span className={styles.survivorTag}>zusammengeführt</span>
            )}
          </div>
          {c.boTeam?.name && (
            <span className={styles.boTeamLine}>
              Verantwortlich (Team): {c.boTeam.name}
            </span>
          )}
          {addr && <span className={styles.memberMeta}>{addr}</span>}
          {(c.defaultEmail || c.defaultPhone) && (
            <span className={styles.memberMeta}>
              {[c.defaultEmail, c.defaultPhone].filter(Boolean).join(" · ")}
            </span>
          )}
          <CountChips summary={c.counts} />
        </div>
      </div>
    );
  };

  const renderCard = (cluster: DuplicateCluster) => {
    const locked = !!cluster.isLocked;
    const tier = scoreTier(cluster.score);
    // A merged cluster has hard-deleted losers (reconstructed snapshots) — there
    // is nothing left to review/merge, so it is shown read-only (no "Prüfen").
    const isMerged = cluster.contacts.some((c) => c.isDeleted);
    return (
      <div
        key={cluster.clusterId}
        className={styles.card}
        style={locked ? { opacity: 0.7 } : undefined}
      >
        <div className={styles.cardHeader}>
          <div
            className={styles.scoreWrap}
            title="Übereinstimmungs-Score (0–100): je höher, desto wahrscheinlicher ein Duplikat"
          >
            <span
              className={styles.scoreCircle}
              style={{ borderColor: tier.color, color: tier.color }}
            >
              {cluster.score}
            </span>
            <span className={styles.scoreCaption}>Score · {tier.label}</span>
          </div>
          <span className={styles.headerReasons}>
            {cluster.contactIds.length} Kontakte ·{" "}
            {cluster.reasons.map((r) => formatReasonCode(r)).join(" · ")}
            {locked && (
              <span className={styles.lockBadge}>
                <LockClosed16Regular style={{ fontSize: 13 }} /> wird bearbeitet
                {cluster.lockedByName ? ` von ${cluster.lockedByName}` : ""}
              </span>
            )}
          </span>
          {!isMerged && (
            <div className={styles.actions}>
              <button
                type="button"
                className={mergeClasses(styles.actionBtn, styles.actionBtnPrimary)}
                disabled={locked || cluster.contactIds.length < 2}
                onClick={() =>
                  navigate(
                    `/contacts/merge?clusterIds=${cluster.contactIds.join(
                      ",",
                    )}&pairIds=${cluster.pairs
                      .map((p) => p.id)
                      .join(",")}&status=${filter}`,
                  )
                }
              >
                <Open20Regular style={{ fontSize: 14 }} /> Prüfen
              </button>
            </div>
          )}
        </div>
        <div className={styles.members}>
          {cluster.contacts.map((c) => renderMember(c))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>
        <ArrowLeft20Regular /> Zurück
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <PeopleSwap20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Duplikatenprüfung</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Wahrscheinliche Doppelgänger im Kontaktstamm, als Cluster gruppiert.
          Zum Bereinigen einen Cluster mit „Prüfen" öffnen.
        </span>
        {isAdmin && (
          <div className={styles.toolbarRow}>
            <button
              type="button"
              className={styles.toolbarBtn}
              disabled={refreshing}
              onClick={handleRefresh}
            >
              <ArrowSync20Regular />
              {refreshing ? "Suche läuft…" : "Erneut suchen"}
            </button>
          </div>
        )}
      </div>

      <div className={styles.searchRow}>
        <Search20Regular style={{ color: BRAND.muted }} />
        <input
          className={styles.searchInput}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche nach Name, Kundennummer oder E-Mail…"
        />
      </div>

      <div className={styles.filterBar}>
        <Filter20Regular style={{ color: BRAND.muted }} />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={mergeClasses(styles.pill, (filter === f.key) && styles.pillActive)}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        {teams.length > 0 && (
          <select
            value={teamId}
            onChange={(e) => setTeamFilter(e.target.value)}
            title="Nach Kontaktverantwortlichem (BO-Team) filtern"
            style={{
              marginLeft: 8,
              padding: "4px 8px",
              borderRadius: 6,
              border: `1px solid ${BRAND.muted}`,
              fontSize: "0.8rem",
              background: "#fff",
              color: BRAND.ink,
            }}
          >
            <option value="">Alle</option>
            <option value="none">Ohne Verantwortlichkeit</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name ?? t.id}
              </option>
            ))}
          </select>
        )}
        <span style={{ color: BRAND.muted, fontSize: "0.8rem", marginLeft: 8 }}>
          {total} Cluster
        </span>
      </div>

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}

      <div
        id="scrollableDivDuplicates"
        style={{ height: "calc(100vh - 320px)", overflowY: "auto" }}
      >
        {loading ? (
          <p className={styles.empty}>Lade Duplikate…</p>
        ) : clusters.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced
              ? "Keine Cluster passen zur Suche."
              : isAdmin
                ? "Keine Cluster in dieser Ansicht. Mit „Erneut suchen“ eine neue Prüfung starten."
                : "Keine Cluster in dieser Ansicht."}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={clusters.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={
              <div style={{ textAlign: "center", color: BRAND.muted }}>
                Lade weitere Cluster…
              </div>
            }
            scrollableTarget="scrollableDivDuplicates"
            style={{ overflow: "visible" }}
          >
            {clusters.map((c) => renderCard(c))}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default ContactDuplicatesPage;
